/**
 * DocumentViewerModal - Internal document viewer for PDFs and images
 * Displays documents inline without downloading using blob URLs
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Download, ZoomIn, ZoomOut, RotateCw,
  FileText, Image, File, Maximize2, Minimize2,
  Loader2
} from 'lucide-react';
import { documentAPI } from '../../api.js';

export default function DocumentViewerModal({
  isOpen,
  onClose,
  doc,
  onDownload
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  // Load blob URL when modal opens
  useEffect(() => {
    let currentBlobUrl = null;

    if (!isOpen || !doc?.id) {
      setBlobUrl(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setZoom(100);
    setRotation(0);

    const loadDocument = async () => {
      try {
        const url = await documentAPI.getBlobUrl(doc.id, doc.mime_type);
        currentBlobUrl = url;
        setBlobUrl(url);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load document:', err);
        setError(err.message || t('common.docs.viewer.loadError'));
        setLoading(false);
      }
    };

    loadDocument();

    // Cleanup on unmount or document change
    return () => {
      if (currentBlobUrl) {
        window.URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [isOpen, doc?.id, doc?.mime_type]);

  if (!isOpen || !doc) return null;

  const isPdf = doc.mime_type === 'application/pdf';
  const isImage = doc.mime_type?.startsWith('image/');
  const canPreview = isPdf || isImage;

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 300));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 25));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleFullscreen = () => setFullscreen(prev => !prev);

  const handleDownload = () => {
    if (onDownload) {
      onDownload(doc);
    } else {
      // Fallback download using documentAPI
      documentAPI.download(doc.id, doc.original_filename || doc.filename);
    }
  };

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] ${fullscreen ? 'p-0' : 'p-4'}`}>
      <div className={`bg-white rounded-lg shadow-2xl flex flex-col ${
        fullscreen ? 'w-full h-full rounded-none' : 'max-w-5xl w-full max-h-[90vh]'
      }`}>
        {/* Header */}
        <div className="bg-gray-800 text-white px-4 py-3 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-3">
            {isPdf && <FileText className="w-5 h-5 text-red-400" />}
            {isImage && <Image className="w-5 h-5 text-green-400" />}
            {!canPreview && <File className="w-5 h-5 text-blue-400" />}
            <div>
              <h3 className="font-medium truncate max-w-md">
                {doc.original_filename || doc.filename}
              </h3>
              <p className="text-xs text-gray-400">
                {doc.mime_type} • {formatSize(doc.file_size)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Zoom controls (for images) */}
            {isImage && (
              <>
                <button
                  onClick={handleZoomOut}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  title={t('common.docs.viewer.zoomOut')}
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-sm min-w-[50px] text-center">{zoom}%</span>
                <button
                  onClick={handleZoomIn}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  title={t('common.docs.viewer.zoomIn')}
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <button
                  onClick={handleRotate}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  title={t('common.docs.viewer.rotate')}
                >
                  <RotateCw className="w-5 h-5" />
                </button>
                <div className="w-px h-6 bg-gray-600 mx-2" />
              </>
            )}

            <button
              onClick={handleFullscreen}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              title={fullscreen ? t('common.docs.viewer.exitFullscreen') : t('common.docs.viewer.fullscreen')}
            >
              {fullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>

            <button
              onClick={handleDownload}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              title={t('common.actions.download')}
            >
              <Download className="w-5 h-5" />
            </button>

            <button
              onClick={onClose}
              className="p-2 hover:bg-red-600 rounded-lg transition-colors ml-2"
              title={t('common.actions.close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-auto bg-gray-100 relative ${fullscreen ? '' : 'rounded-b-lg'}`}>
          {/* Loading indicator */}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                <p className="text-gray-600">{t('common.docs.viewer.loading')}</p>
              </div>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="text-center p-8">
                <File className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('common.states.loadError')}</h4>
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Download className="w-4 h-4 inline mr-2" />
                  {t('documents.validation.downloadFile')}
                </button>
              </div>
            </div>
          )}

          {/* PDF Viewer */}
          {isPdf && !error && blobUrl && (
            <iframe
              src={`${blobUrl}#toolbar=0&navpanes=0`}
              className="w-full h-full min-h-[600px] bg-white"
              title={doc.original_filename || doc.filename}
            />
          )}

          {/* Image Viewer */}
          {isImage && !error && blobUrl && (
            <div className="w-full h-full min-h-[400px] flex items-center justify-center p-4 overflow-auto">
              <img
                src={blobUrl}
                alt={doc.original_filename || doc.filename}
                className="max-w-full max-h-full object-contain transition-transform"
                style={{
                  transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                  transformOrigin: 'center center'
                }}
              />
            </div>
          )}

          {/* Unsupported format */}
          {!canPreview && !error && (
            <div className="w-full h-full min-h-[300px] flex items-center justify-center">
              <div className="text-center p-8">
                <File className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('common.docs.viewer.previewUnavailable')}
                </h4>
                <p className="text-gray-600 mb-4">
                  {t('common.docs.viewer.previewUnavailableDetail', { mime: doc.mime_type })}
                </p>
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Download className="w-4 h-4 inline mr-2" />
                  {t('documents.validation.downloadFile')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper function
function formatSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
